import { FC, useCallback, useMemo, useState } from "react";

import { IPeopleBlockUser, IPeopleBlockUserUpdate } from "Bands/types";
import { copy } from "shared/assets";
import { IActionDropdownItem, Table, TableColumn, Title } from "shared/components";
import { Spacer } from "shared/layout";
import { IStatus } from "shared/types";
import { EditStatusModal } from ".";
import { DeletePeopleBlockUserModal } from "Bands/containers";
import { MemberName, PeopleBlockUserDetail } from "Bands/components";
import { getPeopleBlockStatus } from "Bands/utils";
import { FlexContainer, Icon } from "shared/styles";
import { getStatus } from "shared/styles/defaults";
import { formatDateTime } from "shared/utils";

const peopleBlockCopy = copy.peopleBlock.table;

interface PeopleBlockTableProps {
  blockId: string;
  users?: IPeopleBlockUser[];
}

export const PeopleBlockTable: FC<PeopleBlockTableProps> = ({ users, blockId }) => {
  const [showEditModal, setShowEditModal] = useState<IPeopleBlockUser | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState<IPeopleBlockUser | null>(null);
  const [openRow, setOpenRow] = useState<{ id: string; content: JSX.Element } | undefined>();

  const sortedUsers = useMemo(
    () =>
      users?.sort(({ updatedAt: a }, { updatedAt: b }) => {
        return new Date(a).getTime() > new Date(b).getTime() ? -1 : 1;
      }),
    [users]
  );

  const getRowActions = useCallback(
    (id: string): IActionDropdownItem[] => {
      const peopleBlockUser = users?.find(user => user.id === id);

      if (!peopleBlockUser) return [];

      return [
        {
          label: copy.actions.info,
          handleClick: () => {
            // if current row is open, close the row
            if (openRow?.id === id) {
              setOpenRow(undefined);
              return;
            }

            setOpenRow({
              id,
              content: <PeopleBlockUserDetail {...peopleBlockUser} />,
            });
          },
          dataTestId: "more_info_action",
        },
        {
          label: peopleBlockCopy.actions.editStatus,
          handleClick: () => {
            setOpenRow(undefined);
            setShowEditModal(peopleBlockUser);
          },
          dataTestId: "edit_status_action",
        },
        {
          label: copy.actions.delete,
          handleClick: () => {
            setOpenRow(undefined);
            setShowDeleteModal(peopleBlockUser);
          },
          dataTestId: "delete_action",
          status: IStatus.error,
        },
      ];
    },
    [openRow?.id, users]
  );

  return (
    <Spacer size="m">
      {showEditModal && (
        <EditStatusModal
          blockId={blockId}
          user={showEditModal}
          handleClose={() => setShowEditModal(null)}
        />
      )}
      {showDeleteModal && (
        <DeletePeopleBlockUserModal
          blockId={blockId}
          user={showDeleteModal}
          handleClose={() => setShowDeleteModal(null)}
        />
      )}

      <Title asComponent="h3" size="m">
        {peopleBlockCopy.title}
      </Title>
      <Table
        columns={columns}
        data={sortedUsers}
        emptyCopy={peopleBlockCopy.empty}
        getRowActions={getRowActions}
        openRow={openRow}
        closeOpenRow={() => setOpenRow(undefined)}
      />
    </Spacer>
  );
};

const columns: TableColumn[] = [
  {
    Header: peopleBlockCopy.columns.name,
    accessor: "user",
    Cell: ({ value }) => <MemberName user={value} />,
  },
  {
    Header: peopleBlockCopy.columns.category,
    accessor: "user.category.label",
  },
  {
    Header: peopleBlockCopy.columns.status,
    accessor: "status",
    Cell: ({ value }) => {
      const status = getPeopleBlockStatus(value);

      if (!status) return null;

      return (
        <FlexContainer>
          <Icon as={status.icon} color={getStatus(status.iconStatus).iconColor} />
          <span>{status.viewLabel}</span>
        </FlexContainer>
      );
    },
  },
  {
    Header: peopleBlockCopy.columns.lastUpdate,
    accessor: "updates",
    Cell: ({ value }) => {
      const updates = value as IPeopleBlockUserUpdate[];

      if (!updates?.length) return null;

      const lastUpdate = updates[updates.length - 1];

      return formatDateTime(lastUpdate.date);
    },
  },
];
